import { useState } from 'react';
import { CategoryReviewScore } from '../../../hooks/useReviewRanking';

interface ReviewRankingProps {
  ranking: CategoryReviewScore[];
  onStartQuizByCategory: (subject: string, subcategory: string | null) => void;
}

const RANK_STYLES = [
  { badge: 'bg-gradient-to-br from-yellow-400 to-orange-500', row: 'border-l-2 border-yellow-400', btn: 'bg-red-500 hover:bg-red-600' },
  { badge: 'bg-gradient-to-br from-gray-300 to-gray-400', row: 'border-l-2 border-gray-300', btn: 'bg-orange-500 hover:bg-orange-600' },
  { badge: 'bg-gradient-to-br from-amber-600 to-yellow-700', row: 'border-l-2 border-amber-500', btn: 'bg-amber-500 hover:bg-amber-600' },
];

function formatDate(isoString: string): string {
  const d = new Date(isoString);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

export default function ReviewRanking({ ranking, onStartQuizByCategory }: ReviewRankingProps) {
  const [collapsed, setCollapsed] = useState(false);

  if (ranking.length === 0) return null;

  const top3 = ranking.slice(0, 3);

  const criticalCount = ranking.filter((r) => r.urgency === 'critical').length;
  const highCount = ranking.filter((r) => r.urgency === 'high').length;

  let statusMessage = '';
  let statusIcon = '';
  let statusColor = '';

  if (criticalCount > 0) {
    statusMessage = '復習が必要な状態です';
    statusIcon = 'ri-alarm-warning-fill';
    statusColor = 'text-red-500 dark:text-red-400';
  } else if (highCount > 0) {
    statusMessage = 'やや復習が必要な状態です';
    statusIcon = 'ri-error-warning-fill';
    statusColor = 'text-orange-500 dark:text-orange-400';
  } else if (ranking.length > 0 && ranking[0].totalScore >= 30) {
    statusMessage = '復習を検討してもよい状態です';
    statusIcon = 'ri-information-fill';
    statusColor = 'text-amber-500 dark:text-amber-400';
  } else {
    statusMessage = '良好な状態です';
    statusIcon = 'ri-checkbox-circle-fill';
    statusColor = 'text-teal-600 dark:text-teal-400';
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* ヘッダー（クリックで折りたたみ） */}
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors cursor-pointer select-none"
        onClick={() => setCollapsed((v) => !v)}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-red-400 to-orange-500 rounded-lg shrink-0">
            <i className="ri-brain-line text-sm text-white"></i>
          </div>
          <span className="text-sm font-bold text-gray-800 dark:text-white whitespace-nowrap">復習優先度ランキング</span>
          <span className={`hidden sm:inline-flex items-center gap-1 text-xs font-medium ${statusColor}`}>
            <i className={`${statusIcon} text-xs`}></i>
            {statusMessage}
          </span>
        </div>
        <i className={`ri-arrow-${collapsed ? 'down' : 'up'}-s-line text-base text-gray-400 shrink-0 ml-2`}></i>
      </button>

      {/* モバイル用ステータス */}
      <div className={`sm:hidden px-4 pb-2 ${collapsed ? 'hidden' : ''}`}>
        <span className={`inline-flex items-center gap-1 text-xs font-medium ${statusColor}`}>
          <i className={`${statusIcon} text-xs`}></i>
          {statusMessage}
        </span>
      </div>

      {!collapsed && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          {/* ランキングリスト（上位3位） */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {top3.map((item, index) => {
              const rank = index + 1;
              const style = RANK_STYLES[index];
              const categoryName = item.subcategory ? item.subcategory : item.subject;

              // 経過日数テキスト
              let daysText = '未演習';
              if (item.daysSinceLastStudy !== null) {
                daysText = item.daysSinceLastStudy === 0
                  ? '本日演習済み'
                  : `${item.daysSinceLastStudy}日経過`;
              }

              // 演習日付テキスト
              const dateText = item.lastStudiedAt ? formatDate(item.lastStudiedAt) : null;

              return (
                <div
                  key={item.key}
                  className={`flex items-center gap-3 px-4 py-3 ${style.row} ml-0 pl-3`}
                >
                  {/* 順位バッジ */}
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 ${style.badge}`}>
                    <i className="ri-trophy-fill text-xs text-white"></i>
                  </div>

                  {/* カテゴリ情報 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{rank}位</span>
                      <span className="text-sm font-bold text-gray-800 dark:text-white truncate">{categoryName}</span>
                      {item.subcategory && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 truncate hidden sm:inline">({item.subject})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {/* 初見正答率 */}
                      <span className={`text-xs font-semibold ${
                        item.firstAttemptCorrectRate < 40 ? 'text-red-500' :
                        item.firstAttemptCorrectRate < 70 ? 'text-orange-500' :
                        'text-teal-600 dark:text-teal-400'
                      }`}>
                        初見正答率 {item.firstAttemptCorrectRate}%
                      </span>
                      <span className="text-gray-300 dark:text-gray-600 text-xs">|</span>
                      {/* 演習日付 + 経過日数 */}
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <i className="ri-time-line text-xs"></i>
                        {dateText ? (
                          <>{dateText}（{daysText}）</>
                        ) : (
                          daysText
                        )}
                      </span>
                    </div>
                  </div>

                  {/* 復習ボタン */}
                  <button
                    onClick={() => onStartQuizByCategory(item.subject, item.subcategory)}
                    className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all cursor-pointer whitespace-nowrap ${style.btn}`}
                  >
                    <i className="ri-play-fill text-xs"></i>
                    復習
                  </button>
                </div>
              );
            })}
          </div>

          {/* 4位以降の注記 */}
          {ranking.length > 3 && (
            <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                他に{ranking.length - 3}件の復習推奨カテゴリがあります
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
